import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Package, PanelsTopLeft, ClipboardCheck, CircleUserRound, ScanSearch, ShieldCheck, LogOut, LayoutDashboard } from "lucide-react";
import { NAV_LINKS } from "./config";
import { useSectionNav } from "./useSectionNav";
import { useAuth } from "./AuthContext";
import { Logo } from "./Logo";
import { PrimaryButton } from "./Buttons";

const SERVICE_PAGES = [
  { path: "/business-in-a-box", label: "Business in a Box", desc: "Brand, storefront, and launch — together.", icon: Package, color: "#B8A7E8" },
  { path: "/website-creation", label: "Website Creation", desc: "A custom store, built around your brand.", icon: PanelsTopLeft, color: "#8FD8B8" },
  { path: "/checklist", label: "Launch Checklist", desc: "See what's left and what it costs.", icon: ClipboardCheck, color: "#E8CE8F" },
  { path: "/compliance-checker", label: "Compliance Checker", desc: "Scan a site for launch-risk language.", icon: ScanSearch, color: "#8FBFE8" },
];

const linkFocus =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lilac focus-visible:ring-offset-2 focus-visible:ring-offset-ink";

const AnchorItems = ({ active, onGo, mobile = false, onNavigate }) => (
  <>
    {NAV_LINKS.filter(({ id }) => id !== "services").map(({ id, label }) => {
      const isActive = active === id;
      return (
        <a
          key={id}
          href={`/#${id}`}
          data-testid={mobile ? `mobile-nav-link-${id}` : `nav-link-${id}`}
          aria-current={isActive ? "true" : undefined}
          onClick={onGo(id)}
          className={`relative rounded-md font-bold transition-colors duration-200 ${linkFocus} ${
            mobile
              ? "px-2 py-3 font-display text-3xl font-extrabold"
              : "px-1 py-2 text-sm"
          } ${isActive ? "text-paper" : "text-paper/55 hover:text-paper"}`}
        >
          {label}
          {!mobile && (
            <span
              aria-hidden="true"
              className={`absolute -bottom-0.5 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-lilac transition-opacity duration-200 ${
                isActive ? "opacity-100" : "opacity-0"
              }`}
            />
          )}
        </a>
      );
    })}
    <Link
      to="/resources"
      data-testid={mobile ? "mobile-nav-link-resources" : "nav-link-resources"}
      onClick={onNavigate}
      className={`relative rounded-md font-bold text-paper/55 transition-colors duration-200 hover:text-paper ${linkFocus} ${
        mobile ? "px-2 py-3 font-display text-3xl font-extrabold" : "px-1 py-2 text-sm"
      }`}
    >
      Resources
    </Link>
  </>
);

export const Header = ({ onBuild, menuOpen, setMenuOpen }) => {
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("");
  const [servicesOpen, setServicesOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const accountRef = useRef(null);
  const accountTriggerRef = useRef(null);
  const { user, signout } = useAuth();
  const servicesRef = useRef(null);
  const triggerRef = useRef(null);
  const sectionNav = useSectionNav();
  const { pathname } = useLocation();
  const onServicePage = SERVICE_PAGES.some((p) => pathname.startsWith(p.path));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => entry.isIntersecting && setActive(entry.target.id)),
      { rootMargin: "-30% 0px -60% 0px" }
    );
    NAV_LINKS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [pathname]);

  useEffect(() => {
    setServicesOpen(false);
    setAccountOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!servicesOpen) return undefined;
    const onDown = (e) => {
      if (servicesRef.current && !servicesRef.current.contains(e.target)) setServicesOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setServicesOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [servicesOpen]);

  useEffect(() => {
    if (!accountOpen) return undefined;
    const onDown = (e) => {
      if (accountRef.current && !accountRef.current.contains(e.target)) setAccountOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setAccountOpen(false);
        accountTriggerRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [accountOpen]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const go = (id) => (e) => {
    setMenuOpen(false);
    sectionNav(id)(e);
  };

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-40 transition-[background-color,border-color,backdrop-filter] duration-300 ${
          scrolled
            ? "border-b border-white/[0.07] bg-ink/85 backdrop-blur-md"
            : "border-b border-transparent bg-transparent"
        }`}
      >
        <div className="mx-auto flex h-[72px] w-full max-w-shell items-center justify-between px-5 sm:px-8">
          <a
            href="/"
            data-testid="header-logo"
            onClick={go("top")}
            className={`rounded-md text-paper ${linkFocus}`}
            aria-label="PepMuse — back to home"
          >
            <Logo />
          </a>

          <nav aria-label="Primary" className="hidden items-center gap-9 md:flex">
            <div
              className="relative"
              ref={servicesRef}
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <button
                type="button"
                ref={triggerRef}
                data-testid="nav-services-trigger"
                aria-expanded={servicesOpen}
                aria-haspopup="true"
                aria-current={onServicePage ? "true" : undefined}
                onClick={() => setServicesOpen(true)}
                className={`relative flex items-center gap-1.5 rounded-md px-1 py-2 text-sm font-bold transition-colors duration-200 ${linkFocus} ${
                  onServicePage ? "text-paper" : "text-paper/55 hover:text-paper"
                }`}
              >
                Services
                <ChevronDown
                  className={`h-3.5 w-3.5 transition-transform duration-200 ${servicesOpen ? "rotate-180" : ""}`}
                  aria-hidden="true"
                />
                <span
                  aria-hidden="true"
                  className={`absolute -bottom-0.5 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-lilac transition-opacity duration-200 ${
                    onServicePage ? "opacity-100" : "opacity-0"
                  }`}
                />
              </button>

              {servicesOpen && (
                <div
                  data-testid="nav-services-menu"
                  className="absolute left-1/2 top-full z-50 w-[21rem] -translate-x-1/2 pt-3"
                >
                  <div className="rounded-2xl border border-white/10 bg-card p-2 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.7)]">
                    {SERVICE_PAGES.map((page) => {
                      const Icon = page.icon;
                      const current = pathname.startsWith(page.path);
                      return (
                        <Link
                          key={page.path}
                          to={page.path}
                          data-testid={`nav-service-${page.path.slice(1)}`}
                          aria-current={current ? "page" : undefined}
                          onClick={() => setServicesOpen(false)}
                          className="group flex items-start gap-3.5 rounded-xl p-3.5 transition-colors duration-200 hover:bg-white/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-lilac"
                        >
                          <span
                            className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-ink transition-transform duration-200 group-hover:-translate-y-0.5"
                            style={{ backgroundColor: page.color }}
                          >
                            <Icon className="h-4 w-4" strokeWidth={2.4} aria-hidden="true" />
                          </span>
                          <span>
                            <span className="block text-sm font-bold text-paper">
                              {page.label}
                              {current && <span className="ml-2 text-xs font-bold text-lilac">You're here</span>}
                            </span>
                            <span className="mt-0.5 block text-xs text-paper/50">{page.desc}</span>
                          </span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <AnchorItems active={active} onGo={go} />
          </nav>

          <div className="flex items-center gap-3">
            <PrimaryButton testId="header-build-button" onClick={onBuild} className="hidden whitespace-nowrap !px-6 !py-2.5 sm:inline-flex">
              Request a launch proposal
            </PrimaryButton>
            {user ? (
              <div className="relative" ref={accountRef}>
                <button
                  type="button"
                  ref={accountTriggerRef}
                  data-testid="account-menu-trigger"
                  aria-expanded={accountOpen}
                  aria-haspopup="true"
                  aria-label="Account menu"
                  onClick={() => setAccountOpen((v) => !v)}
                  className={`inline-flex h-11 w-11 items-center justify-center overflow-hidden rounded-full border border-white/10 font-display text-sm font-extrabold text-lilac transition-colors duration-200 hover:border-lilac/50 ${linkFocus} ${user.picture ? "" : "bg-[#1c1e2d]"}`}
                >
                  {user.picture ? (
                    <img src={user.picture} alt="" className="h-full w-full object-cover" referrerPolicy="no-referrer" />
                  ) : (
                    <img src="/favicon.png" alt="" className="h-6 w-6 rounded-md" />
                  )}
                </button>
                {accountOpen && (
                  <div
                    data-testid="account-menu"
                    className="absolute right-0 top-full z-50 mt-3 w-64 rounded-2xl border border-white/10 bg-card p-2 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.7)]"
                  >
                    <div className="border-b border-white/[0.07] px-3.5 pb-3 pt-2">
                      <p className="truncate text-sm font-bold text-paper">{user.name || "Account"}</p>
                      <p className="truncate text-xs text-paper/45">{user.email}</p>
                    </div>
                    <Link
                      to="/account"
                      data-testid="account-menu-dashboard"
                      onClick={() => setAccountOpen(false)}
                      className="mt-1.5 flex items-center gap-2.5 rounded-xl px-3.5 py-2.5 text-sm font-bold text-paper/70 transition-colors duration-200 hover:bg-white/[0.04] hover:text-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-lilac"
                    >
                      <LayoutDashboard className="h-4 w-4 text-lilac" aria-hidden="true" />
                      Your dashboard
                    </Link>
                    {user.role === "admin" && (
                      <Link
                        to="/admin"
                        data-testid="account-menu-admin-link"
                        onClick={() => setAccountOpen(false)}
                        className="flex items-center gap-2.5 rounded-xl px-3.5 py-2.5 text-sm font-bold text-paper/70 transition-colors duration-200 hover:bg-white/[0.04] hover:text-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-lilac"
                      >
                        <ShieldCheck className="h-4 w-4 text-lilac" aria-hidden="true" />
                        Admin Panel →
                      </Link>
                    )}
                    <button
                      type="button"
                      data-testid="account-menu-signout"
                      onClick={() => {
                        setAccountOpen(false);
                        signout();
                      }}
                      className="flex w-full items-center gap-2.5 rounded-xl px-3.5 py-2.5 text-left text-sm font-bold text-paper/70 transition-colors duration-200 hover:bg-white/[0.04] hover:text-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-lilac"
                    >
                      <LogOut className="h-4 w-4 text-paper/40" aria-hidden="true" />
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/account"
                data-testid="header-account-link"
                aria-label="Account"
                className={`inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/10 text-paper transition-colors duration-200 hover:border-lilac/50 hover:text-lilac ${linkFocus}`}
              >
                <CircleUserRound className="h-5 w-5" aria-hidden="true" />
              </Link>
            )}
            <button
              type="button"
              data-testid="mobile-menu-button"
              aria-expanded={menuOpen}
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              onClick={() => setMenuOpen((v) => !v)}
              className={`inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/10 text-paper transition-colors duration-200 hover:border-lilac/50 hover:text-lilac md:hidden ${linkFocus}`}
            >
              {menuOpen ? <X className="h-5 w-5" aria-hidden="true" /> : <Menu className="h-5 w-5" aria-hidden="true" />}
            </button>
          </div>
        </div>
      </header>

      {menuOpen && (
        <div
          data-testid="mobile-menu"
          className="fixed inset-0 z-30 flex flex-col justify-between overflow-y-auto bg-ink px-6 pb-10 pt-24 md:hidden"
        >
          <nav aria-label="Mobile" className="flex flex-col">
            <p className="px-2 text-xs font-bold uppercase tracking-[0.22em] text-paper/40">Services</p>
            <div className="mt-2 flex flex-col gap-1">
              {SERVICE_PAGES.map((page) => {
                const Icon = page.icon;
                const current = pathname.startsWith(page.path);
                return (
                  <Link
                    key={page.path}
                    to={page.path}
                    data-testid={`mobile-nav-service-${page.path.slice(1)}`}
                    aria-current={current ? "page" : undefined}
                    onClick={() => setMenuOpen(false)}
                    className={`flex items-center gap-3.5 rounded-xl px-2 py-2.5 font-display text-2xl font-extrabold transition-colors duration-200 ${linkFocus} ${
                      current ? "text-paper" : "text-paper/70 hover:text-paper"
                    }`}
                  >
                    <span
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-ink"
                      style={{ backgroundColor: page.color }}
                    >
                      <Icon className="h-4 w-4" strokeWidth={2.4} aria-hidden="true" />
                    </span>
                    {page.label}
                  </Link>
                );
              })}
            </div>
            <div className="my-5 border-t border-white/[0.08]" />
            <AnchorItems active={active} onGo={go} mobile onNavigate={() => setMenuOpen(false)} />
            <Link
              to="/account"
              data-testid="mobile-nav-account"
              onClick={() => setMenuOpen(false)}
              className={`mt-1 rounded-md px-2 py-3 font-display text-3xl font-extrabold text-paper/55 transition-colors duration-200 hover:text-paper ${linkFocus}`}
            >
              Account
            </Link>
            {user?.role === "admin" && (
              <Link
                to="/admin"
                data-testid="mobile-nav-admin"
                onClick={() => setMenuOpen(false)}
                className={`flex items-center gap-3 rounded-md px-2 py-3 font-display text-3xl font-extrabold text-lilac transition-colors duration-200 hover:text-lilac-bright ${linkFocus}`}
              >
                <ShieldCheck className="h-6 w-6" aria-hidden="true" />
                Admin Panel →
              </Link>
            )}
          </nav>
          <PrimaryButton
            testId="mobile-menu-build-button"
            onClick={() => {
              setMenuOpen(false);
              onBuild();
            }}
            className="mt-8 w-full"
          >
            Request a launch proposal
          </PrimaryButton>
        </div>
      )}
    </>
  );
};
